/**
 * Captura de errores no controlados.
 *
 * El ErrorBoundary de React solo ve lo que revienta **durante el render**. Se
 * le escapan los errores asincrónicos: un `.then()` que falla, un handler de
 * evento, un callback de una librería. Esos hoy no dejan ningún rastro — y si
 * uno de ellos deja la pantalla inutilizable, nos enteramos solo si el usuario
 * se toma el trabajo de avisar.
 *
 * Esto no arregla nada: instrumenta. Convierte un fantasma irreproducible en un
 * reporte con ruta, mensaje y stack.
 */
import { track } from '@/lib/analytics';

let instalado = false;

function reportar(tipo: string, message: string, stack?: string) {
  try {
    track('app_error', {
      tipo,
      message: message.slice(0, 500),
      stack: stack?.slice(0, 2000),
      path: window.location.pathname,
    });
  } catch {
    // Nunca dejar que el reporte de un error genere otro error.
  }
}

export function installErrorReporting() {
  if (instalado) return;
  instalado = true;

  window.addEventListener('error', (e) => {
    // Los errores de carga de recursos (img, script) llegan acá sin `error`.
    // Interesan igual: un chunk que no carga se ve así.
    if (e.error instanceof Error) {
      reportar('window_error', e.error.message, e.error.stack);
    } else if (e.target && e.target !== window) {
      const el = e.target as HTMLElement & { src?: string; href?: string };
      reportar('resource_error', `No cargó: ${el.src ?? el.href ?? el.tagName}`);
    }
  }, true); // captura: los errores de recursos no burbujean

  window.addEventListener('unhandledrejection', (e) => {
    const r = e.reason;
    reportar(
      'unhandled_rejection',
      r instanceof Error ? r.message : String(r),
      r instanceof Error ? r.stack : undefined
    );
  });
}
